import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useQueryParamStore } from '@/composables/query-param-store/query-param-store'

export type CardKey = string
export type RedirectMap = {
  [key: string]: {
    to: CardKey
  }
}

/**
 * Creates a Pinia store for managing active cards with query parameter synchronization.
 *
 * @param CARD_KEYS - An array of valid card keys.
 * @param CARD_REDIRECTS - A map of redirects for card keys.
 */
export function createCardsStore(
  CARD_KEYS: readonly CardKey[],
  CARD_REDIRECTS: RedirectMap
) {
  return defineStore('cards', () => {
    // The cards that are currently active (open).
    const activeCards = ref<Map<CardKey, string | undefined>>(new Map())
    useQueryParamStore<Map<CardKey, string | undefined>>({
      param: 'cards',
      parse: parseCardsParam,
      serialize: serializeCardsParam,
      onInitialize: (parsed) => {
        activeCards.value = parsed
      },
      getValue: () => activeCards.value,
      mode: 'sync',
    })

    /**
     * Toggles the active state of a card (open and close).
     *
     * @param key - The key of the card to toggle.
     * @param [disabled=false] - If true, the toggle will not change the state.
     * @param [param] - Optional parameter to associate with the card.
     * Example: ?cards=layer_detail_card:dispersed_camping, where layer_detil_card is the key and dispersed_camping is the parameter.
     */
    function toggleCard(key: CardKey, disabled = false, param?: string) {
      if (disabled) {
        return
      }
      if (activeCards.value.has(key)) {
        activeCards.value.delete(key)
      } else {
        activeCards.value.set(key, param)
      }
      activeCards.value = new Map(activeCards.value)
    }

    /**
     * Sets a card as active or inactive.
     *
     * @param key - The key of the card to set active or inactive.
     * @param active - If true, the card will be set as active; if false, it will be set as inactive.
     * @param [param] - Optional parameter to associate with the card.
     */
    function setCardState(key: string, active: boolean, param?: string) {
      const redirect = CARD_REDIRECTS[key]
      const finalKey = redirect?.to || (isValidCardKey(key) ? key : null)
      const finalParam = param

      if (!finalKey || !isValidCardKey(finalKey)) {
        return
      }

      if (active) {
        activeCards.value.set(finalKey, finalParam)
      } else {
        activeCards.value.delete(finalKey)
      }
      activeCards.value = new Map(activeCards.value)
    }

    /**
     * Checks if a card is currently active.
     *
     * @param key - The key of the card to check.
     * @returns - Returns true if the card is active, false otherwise.
     */
    function isCardActive(key: CardKey): boolean {
      return activeCards.value.has(key)
    }

    /**
     * Gets the parameter associated with a card.
     *
     * @param key - The key of the card to get the parameter for.
     * @returns The parameter if it exists, otherwise undefined.
     */
    function getCardParam(key: CardKey): string | undefined {
      return activeCards.value.get(key)
    }

    // Helper functions

    function isValidCardKey(key: string): key is CardKey {
      return (CARD_KEYS as readonly string[]).includes(key)
    }

    function parseCardsParam(value: string): Map<CardKey, string | undefined> {
      const map = new Map<CardKey, string | undefined>()
      let redirected = false

      value.split(',').forEach((card) => {
        const [key, param] = card.split(':')
        const redirect = CARD_REDIRECTS[key]
        const finalKey = redirect?.to || key

        if (redirect) {
          redirected = true
        }

        if (isValidCardKey(finalKey)) {
          map.set(finalKey, param)
        }
      })

      // Only update the URL if a redirect actually occurred
      if (redirected) {
        const newParam = serializeCardsParam(map)
        const url = new URL(window.location.href)
        url.searchParams.set('cards', newParam)
        window.history.replaceState({}, '', url.toString())
      }

      return map
    }

    function serializeCardsParam(
      map: Map<CardKey, string | undefined>
    ): string {
      return Array.from(map.entries())
        .map(([key, param]) => (param ? `${key}:${param}` : key))
        .join(',')
    }

    return {
      activeCards,
      toggleCard,
      setCardState,
      isCardActive,
      getCardParam,
    }
  })
}
